/**
 * Chunk C0 — Credit note *documents* (Invoices library), not GR “Ask for credit?”.
 *
 * Locked product stance:
 * - CN stays a document (not a negative order)
 * - Suggest / attach (= apply) to a PO; never one-click reconcile the CN alone
 * - Soft-link to the sole invoice on that PO when there is exactly one (v1.5)
 * - PO Reconciled needs ≥1 invoice + net ~0; CN-only → “waiting for invoice”
 *
 * C0: helpers + copy pack.
 * C1+: teaching surfaces wire CN_DOCUMENT_COPY (upload, Matching, Orders, Ask-for-credit, coach).
 * C2: Apply to PO via link/attach + silent softLinkedInvoiceId when exactly one invoice.
 * C3: net ambient + reconcile ≥1 invoice gate; rematch peers after CN apply.
 * C4: under-tolerance / over-balance CN coach suggestion helper.
 */

import { formatInvoiceTotalDisplay } from './invoiceUploadHelpers'
import { getLinkedInvoicesForReceiptingOrder } from './reconciliationHealth'
import { isVoidedGoodsReceiptingOrder } from './goodsReceiptingVoidHelpers'

/** Soft-link target when applying a CN to a PO (Chunk C2). */
export const CN_SOFT_LINK_KIND = {
  /** No invoice on the PO yet — apply CN; wait for invoice to reconcile. */
  NONE: 'none',
  /** Exactly one invoice — silent soft-link CN → that invoice (Xero later). */
  SINGLE: 'single',
  /** Two or more invoices — PO-only; no invoice soft-link in v1. */
  MULTIPLE: 'multiple',
}

/**
 * Teaching + action copy for CN documents.
 * Wire in C1+; keep wording stable so tests lock the pack.
 */
export const CN_DOCUMENT_COPY = {
  glossary:
    'Credit note = reduction in what you owe · Invoice = what they billed.',

  uploadConfirm:
    'Credit notes reduce what you owe on a PO. Attach them to the order — don’t create a new order.',

  matchingChipLabel: 'Credit note — apply to a PO',
  matchingGuidance:
    'This won’t match the PO total on its own — apply it to the PO. Credits offset over-billing; they do not replace an invoice.',

  applyActionLabel: 'Apply to PO',
  applyActionTooltip:
    'Attach this credit note to the purchase order. It offsets invoice over-billing on that PO — it does not replace an invoice.',

  addOrderNudge:
    'Need to record a supplier credit? Upload a Credit Note in Invoices — don’t add a negative order.',

  askForCreditFollowUp:
    'When the supplier sends the credit note PDF, upload it as Credit Note and attach it to this PO.',

  underToleranceCnHint:
    'This doesn’t close the PO yet. If the supplier sent a credit for a short or damaged delivery, upload it as a Credit Note and attach it here — then the balance can net to zero.',

  doubleCountWarn:
    'This PO was receipted short. The received total already reflects the shortage — applying a credit note for the same short may double-count. Prefer matching the invoice to the received total, or ordered total + credit note — not both.',

  uploadCreditNoteCta: 'Upload Credit Note',
  findCreditNoteCta: 'Open Invoices & Credit Notes',

  emptyStateHelp:
    'Got a credit from the supplier for a short or damaged delivery? Upload it as Credit Note and attach it to that PO — don’t create a new order.',

  coachModalTitle: 'How credit notes work',
  coachModalBody:
    'A credit note reduces what you owe on a purchase order. Upload it as Credit Note, apply it to that PO, then match the invoice — when the invoice covers what was received (or invoice + credit nets to received), you can reconcile. Don’t create a new order.',

  waitingForInvoiceSuffix: 'waiting for invoice',

  applyBlockedNotCreditNote:
    'Only credit note documents can be applied this way.',
  applyBlockedNoOrder: 'Choose a purchase order to apply this credit to.',
  applyBlockedVoided:
    'This order is voided — restore it before applying a credit note.',
  applyBlockedArchived: 'Restore this credit note before applying it to a PO.',
  applyBlockedFinanceClosed:
    'This credit note is already reconciled or in Xero.',
  applyBlockedAlreadyOnOtherPo:
    'This credit note is already applied to another purchase order. Remove it there first.',
}

const FINANCE_CLOSED = new Set(['reconciled', 'sentToXero'])

function normalisePo(value) {
  return String(value || '').trim()
}

function invoiceIdKey(invoice = {}) {
  return String(invoice._id || invoice.id || '').trim()
}

/**
 * True when the library document is a credit note (upload type or OCR detect).
 * Accepts creditNote / credit_note / “credit note” (any casing).
 */
export function isCreditNoteDocument(doc = {}) {
  const type = String(doc?.documentType || doc?.detectedDocumentType || '')
    .trim()
    .toLowerCase()
  return (
    type === 'creditnote' || type === 'credit_note' || type === 'credit note'
  )
}

/** Absolute magnitude of a CN total in pennies (CNs are stored signed negative). */
export function getCreditNoteAbsolutePennies(doc = {}) {
  const raw = doc?.invoiceTotal ?? doc?.totalPennies ?? doc?.total
  if (raw == null || raw === '') return null
  const num = Number(raw)
  if (!Number.isFinite(num)) return null
  return Math.abs(Math.round(num))
}

/**
 * Invoices (not credit notes) already soft-linked / closed on this PO.
 * Used to decide silent CN → invoice soft-link (0 / 1 / many).
 */
export function listInvoicesOnPurchaseOrderForCreditApply(
  order = {},
  uploadedInvoices = [],
) {
  return getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices).filter(
    invoice => !isCreditNoteDocument(invoice),
  )
}

/**
 * Soft-link target when applying a CN to this PO.
 * @returns {{ kind: 'none'|'single'|'multiple', invoice: object|null }}
 */
export function getCreditNoteSoftLinkTarget(order = {}, uploadedInvoices = []) {
  const invoices = listInvoicesOnPurchaseOrderForCreditApply(
    order,
    uploadedInvoices,
  )
  if (invoices.length === 0) {
    return { kind: CN_SOFT_LINK_KIND.NONE, invoice: null }
  }
  if (invoices.length === 1) {
    return { kind: CN_SOFT_LINK_KIND.SINGLE, invoice: invoices[0] }
  }
  return { kind: CN_SOFT_LINK_KIND.MULTIPLE, invoice: null }
}

/**
 * Whether Apply to PO is allowed (gates only — no API side effects).
 */
export function canApplyCreditNoteToPurchaseOrder(creditNote = {}, order = {}) {
  return (
    getApplyCreditNoteToPurchaseOrderDisabledReason(creditNote, order) == null
  )
}

export function getApplyCreditNoteToPurchaseOrderDisabledReason(
  creditNote = {},
  order = {},
) {
  if (!isCreditNoteDocument(creditNote)) {
    return CN_DOCUMENT_COPY.applyBlockedNotCreditNote
  }
  if (creditNote?.isArchived) {
    return CN_DOCUMENT_COPY.applyBlockedArchived
  }
  if (FINANCE_CLOSED.has(String(creditNote?.status || '').trim())) {
    return CN_DOCUMENT_COPY.applyBlockedFinanceClosed
  }
  const po = normalisePo(order?.purchaseOrderNumber)
  if (!po) {
    return CN_DOCUMENT_COPY.applyBlockedNoOrder
  }
  if (isVoidedGoodsReceiptingOrder(order)) {
    return CN_DOCUMENT_COPY.applyBlockedVoided
  }

  const existingPo = normalisePo(creditNote?.purchaseOrderNumber)
  if (existingPo && existingPo !== po) {
    const lifecycle = String(creditNote?.status || '').trim()
    if (lifecycle === 'attachedToPO' || FINANCE_CLOSED.has(lifecycle)) {
      return CN_DOCUMENT_COPY.applyBlockedAlreadyOnOtherPo
    }
  }

  return null
}

/**
 * Remaining-line / status strip when a CN is on the PO but no invoice yet.
 * @param {number|null} creditPenniesAbs - absolute pennies (positive)
 */
export function getCreditAppliedWaitingForInvoiceCopy(creditPenniesAbs) {
  const amount =
    creditPenniesAbs == null
      ? '—'
      : formatInvoiceTotalDisplay(-Math.abs(Number(creditPenniesAbs)))
  return `Credit applied ${amount} · ${CN_DOCUMENT_COPY.waitingForInvoiceSuffix}`
}

/**
 * Success toast / banner after apply (C2).
 * @param {number|null} creditPenniesAbs
 * @param {number|null} remainingPennies - signed remaining after apply
 */
export function getCreditNoteApplySuccessCopy(
  creditPenniesAbs,
  remainingPennies = null,
  { waitingForInvoice = false } = {},
) {
  const credit =
    creditPenniesAbs == null
      ? '—'
      : formatInvoiceTotalDisplay(-Math.abs(Number(creditPenniesAbs)))
  if (remainingPennies == null) {
    const base = `Credit applied (${credit}).`
    return waitingForInvoice
      ? `${base} · ${CN_DOCUMENT_COPY.waitingForInvoiceSuffix}`
      : base
  }
  const remaining = formatInvoiceTotalDisplay(remainingPennies)
  const base = `Credit applied (${credit}). Remaining: ${remaining}`
  return waitingForInvoice
    ? `${base} · ${CN_DOCUMENT_COPY.waitingForInvoiceSuffix}`
    : base
}

/**
 * Compact remaining ambient when invoices + credits are both present (C3).
 */
export function formatPoDocumentNetSummaryLine({
  invoicePennies = null,
  creditPenniesAbs = null,
  remainingPennies = null,
} = {}) {
  const parts = []
  if (invoicePennies != null && Number.isFinite(Number(invoicePennies))) {
    parts.push(`Invoice(s) ${formatInvoiceTotalDisplay(invoicePennies)}`)
  }
  if (creditPenniesAbs != null && Number.isFinite(Number(creditPenniesAbs))) {
    parts.push(
      `Credit(s) ${formatInvoiceTotalDisplay(
        -Math.abs(Number(creditPenniesAbs)),
      )}`,
    )
  }
  if (remainingPennies != null && Number.isFinite(Number(remainingPennies))) {
    parts.push(`Remaining ${formatInvoiceTotalDisplay(remainingPennies)}`)
  }
  return parts.join(' · ')
}

const LINKED_CONTRIBUTION_STATUSES = new Set([
  'attachedToPO',
  'reconciled',
  'sentToXero',
])

/**
 * Sum signed invoice + credit totals from documents already on a PO.
 */
export function sumPoDocumentNetPennies(linkedDocuments = []) {
  let invoicePennies = 0
  let creditPenniesAbs = 0
  let invoiceCount = 0
  let creditCount = 0

  for (const doc of linkedDocuments || []) {
    if (!doc || doc.isArchived) continue
    if (!LINKED_CONTRIBUTION_STATUSES.has(String(doc.status || '').trim())) {
      continue
    }
    const raw = doc.invoiceTotal ?? doc.totalPennies
    if (raw == null || raw === '') continue
    const num = Number(raw)
    if (!Number.isFinite(num)) continue

    if (isCreditNoteDocument(doc)) {
      creditPenniesAbs += Math.abs(Math.round(num))
      creditCount += 1
    } else {
      invoicePennies += Math.abs(Math.round(num))
      invoiceCount += 1
    }
  }

  return {
    invoicePennies: invoiceCount ? invoicePennies : null,
    creditPenniesAbs: creditCount ? creditPenniesAbs : null,
    invoiceCount,
    creditCount,
  }
}

/**
 * GR / Match ambient line for a PO's document mix (C3).
 * CN-only → waiting copy; invoice+credit → net summary; else empty (caller
 * may fall back to outstanding-only hint).
 */
export function buildPoNetAmbientSummary({
  linkedDocuments = [],
  remainingPennies = null,
} = {}) {
  const sums = sumPoDocumentNetPennies(linkedDocuments)

  if (sums.creditCount > 0 && sums.invoiceCount === 0) {
    return getCreditAppliedWaitingForInvoiceCopy(sums.creditPenniesAbs)
  }

  if (sums.creditCount > 0 && sums.invoiceCount > 0) {
    return formatPoDocumentNetSummaryLine({
      invoicePennies: sums.invoicePennies,
      creditPenniesAbs: sums.creditPenniesAbs,
      remainingPennies,
    })
  }

  return ''
}

/**
 * True when PO reconcile-close is allowed by document mix (product lock):
 * need ≥1 non-CN invoice; CN-only never counts as Reconciled.
 */
export function purchaseOrderHasInvoiceForReconcileClose(
  order = {},
  uploadedInvoices = [],
) {
  return (
    listInvoicesOnPurchaseOrderForCreditApply(order, uploadedInvoices).length >
    0
  )
}

/** Stable id helper for soft-link payload builders (C2). */
export function getCreditNoteSoftLinkInvoiceId(
  order = {},
  uploadedInvoices = [],
) {
  const target = getCreditNoteSoftLinkTarget(order, uploadedInvoices)
  if (target.kind !== CN_SOFT_LINK_KIND.SINGLE || !target.invoice) {
    return null
  }
  const id = invoiceIdKey(target.invoice)
  return id || null
}

// --- Chunk C4: under-tolerance / over-balance CN coach ---

const OUTSIDE_TOLERANCE_FAILURE = 'total_outside_tolerance'
const CREDIT_DISPUTE_RECEIPTING_STATUSES = new Set([
  'CN Requested',
  'Sent to supplier',
  'Rejected by supplier',
])
const TERMINAL_INVOICE_LIFECYCLES = new Set(['reconciled', 'sentToXero'])

/**
 * GR order still in credit-request / dispute workflow (Chunk C4 signal).
 */
export function orderHasCreditDisputeSignal(order = {}) {
  if (!order) return false
  const status = String(order.receiptingStatus || '').trim()
  if (CREDIT_DISPUTE_RECEIPTING_STATUSES.has(status)) return true
  // Line-level Sent to supplier when parent status was rolled up oddly.
  const lines = Array.isArray(order.products) ? order.products : []
  return lines.some(line => {
    const lineStatus = String(
      line?.status || line?.receiptingStatus || '',
    ).trim()
    return lineStatus === 'Sent to supplier' || lineStatus === 'CN Requested'
  })
}

/** Invoice billed more than open PO balance beyond tolerance. */
export function isInvoiceOverCoveringPo(invoice = {}) {
  if (invoice.matchingStatus !== 'needs_action') return false
  if (
    invoice.matchingFailureReason === OUTSIDE_TOLERANCE_FAILURE ||
    invoice.matchingFailureReason === 'po_already_fully_invoiced'
  ) {
    return true
  }
  const remaining = invoice.matchSummary?.remainingBalancePennies
  const tolerance = Math.max(
    0,
    Number(invoice.matchSummary?.tolerancePennies) || 0,
  )
  return (
    remaining != null &&
    Number.isFinite(Number(remaining)) &&
    Number(remaining) < -tolerance
  )
}

/** Partial under-cover (more invoices expected) — not over-bill. */
export function isInvoicePartialUnderCover(invoice = {}) {
  return invoice.matchingStatus === 'partial_match'
}

/**
 * Whether Match should coach the credit-note path for a stuck invoice.
 *
 * Show when:
 * - over-covered / outside tolerance (failing close), or
 * - partial under-cover **with** credit-dispute / Ask-for-credit signals
 *
 * Hide for clean partial under-cover without dispute signals.
 *
 * Soft-warn double-count when receipted short already baked into received
 * total and a CN for the same short is in play.
 *
 * @returns {{
 *   show: boolean,
 *   reason: string|null,
 *   hint: string,
 *   followUp: string|null,
 *   doubleCountWarn: boolean,
 *   doubleCountMessage: string|null,
 *   uploadCtaLabel: string,
 *   browseCtaLabel: string,
 * }}
 */
export function getInvoiceCreditNoteCoachSuggestion({
  invoice = {},
  order = null,
  linkedDocuments = [],
} = {}) {
  const empty = {
    show: false,
    reason: null,
    hint: CN_DOCUMENT_COPY.underToleranceCnHint,
    followUp: null,
    doubleCountWarn: false,
    doubleCountMessage: null,
    uploadCtaLabel: CN_DOCUMENT_COPY.uploadCreditNoteCta,
    browseCtaLabel: CN_DOCUMENT_COPY.findCreditNoteCta,
  }

  if (!invoice || isCreditNoteDocument(invoice)) return empty
  if (TERMINAL_INVOICE_LIFECYCLES.has(String(invoice.status || '').trim())) {
    return empty
  }

  const overCovered = isInvoiceOverCoveringPo(invoice)
  const partial = isInvoicePartialUnderCover(invoice)
  const dispute = orderHasCreditDisputeSignal(order)
  const hasAppliedCn = (linkedDocuments || []).some(
    doc =>
      isCreditNoteDocument(doc) &&
      LINKED_CONTRIBUTION_STATUSES.has(String(doc.status || '').trim()),
  )

  let show = false
  let reason = null

  if (overCovered) {
    show = true
    reason = dispute ? 'over_covered_with_credit_request' : 'over_covered'
  } else if (partial && dispute) {
    show = true
    reason = 'partial_with_credit_request'
  }

  if (!show) return empty

  const doubleCountWarn =
    overCovered &&
    invoice.matchSummary?.orderGrossBasis === 'receipted' &&
    (dispute || hasAppliedCn)

  return {
    show: true,
    reason,
    hint: CN_DOCUMENT_COPY.underToleranceCnHint,
    followUp: dispute ? CN_DOCUMENT_COPY.askForCreditFollowUp : null,
    doubleCountWarn,
    doubleCountMessage: doubleCountWarn
      ? CN_DOCUMENT_COPY.doubleCountWarn
      : null,
    uploadCtaLabel: CN_DOCUMENT_COPY.uploadCreditNoteCta,
    browseCtaLabel: CN_DOCUMENT_COPY.findCreditNoteCta,
  }
}
